import React, { useContext, useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import { CreditCard, Glasses, User2Icon, UserIcon } from "lucide-react";
import { base_url } from "../../config";
import axios from "axios";
import RegistrationContext from "../../contexts/RegistrationContext";
import RegistrationDetail from "./RegistrationDetail";
import BadgeControl from "./BadgeControl";

const statusFilters = [
  { label: "All", value: "" },
  { label: "Completed", value: "Completed" },
  { label: "In progress", value: "In progress" },
];

function toDateAssumingUTC(input) {
  // Epoch ms or Date -> return as-is
  if (input instanceof Date) return input;
  if (typeof input === "number") return new Date(input); // assume ms

  const s = String(input).trim();

  // If it already has a timezone (Z or ±HH:MM), parse normally
  if (/Z$|[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);

  // If it's like "2025-08-16 15:30:00" (no TZ), treat as UTC:
  // normalize to ISO and append Z
  // also handle already-ISO without Z: "2025-08-16T15:30:00"
  const isoish = s.replace(" ", "T");
  return new Date(isoish + "Z");
}

const UserTable = () => {
  const [data, setData] = useState([]);
  const [typeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Completed");

  const { reload, setReload, permissions } = useContext(RegistrationContext);
  const [openModal, setOpenModal] = useState(false);
  const [openModalBadges, setOpenModalBadges] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [userData, setUserData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);



  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/registrations`)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching students:", error));
  }, []);

  useEffect(() => {
    setFilteredData(
      data.filter((row) => {
        const typeMatch = typeFilter ? row.type === typeFilter : true;
        const statusMatch = statusFilter
          ? statusFilter === "Completed"
            ? row.status === "Completed"
            : ["In progress", "in-progress", "progress"].includes(row.status)
          : true;
        return typeMatch && statusMatch;
      })
    );
  }, [data, typeFilter, statusFilter]);

  const handler = () => {
    // Open your badge control modal/page

    axios
      .get(`${base_url}/api/admin/registrations`)
      .then((response) => setData(response.data))
      .catch((error) => console.error("Error fetching data:", error));
  };

  const columns = [
    {
      accessorKey: "status",
      // Remove header and actions for Status column
      header: "",
      enableColumnActions: false,
      enableSorting: false,
      size: 40, // minimal width in px
      maxSize: 40,
      minSize: 40,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        if (value === "Completed") {
          // Blue check mark
          return (
            <span className="flex items-center justify-center">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </span>
          );
        }
        if (
          value === "In progress" ||
          value === "in-progress" ||
          value === "progress"
        ) {
          // Yellow dot
          return (
            <span className="flex items-center justify-center">
              <span className="h-3 w-3 rounded-full bg-yellow-400 inline-block"></span>
            </span>
          );
        }
        // Default: gray dot
        return (
          <span className="flex items-center justify-center">
            <span className="h-3 w-3 rounded-full bg-gray-300 inline-block"></span>
          </span>
        );
      },
    },
    {
      accessorKey: "code",
      header: "Code",
      size: 80, // minimal width in px
      maxSize: 80,
      minSize: 80,
      Cell: ({ cell }) => {
        const value = cell.getValue();

        return (
          <span className={`font-mono text-xs bg-gray-50 px-2 py-1 rounded cursor-pointer`} >
            {value}
          </span>
        )
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      size: 90, // minimal width in px
      maxSize: 90,
      minSize: 90,
      Cell: ({ cell }) => {
        const value = cell.getValue();
        let color = "";
        let label = "";
        let label2nd = "";
        switch (value) {
          case "industry":
            color = "bg-white-100 text-yellow-800 border-yellow-300";
            label = "Industry";
            break;
          case "regular":
            color = "bg-white text-blue-800 border-blue-300";
            label = "Regular";
            break;
          case "student":
            color = "bg-white text-green-800 border-green-300";
            label = "Student";
            break;
          case "student-paper":
            color = "bg-white text-purple-800 border-purple-300";
            label = "Student Paper";
            break;
          case "exhibitor":
            color = "bg-white text-orange-800 border-orange-300";
            label = "Exhibitor";
            break;
          default:
            color = "bg-gray-100 text-gray-800 border-gray-300";
            label = value;
        }
        return (
          <div
            className={`px-2 py-1 rounded border text-xs font-semibold text-center ${color}`}
          >
            <>
              {label}
              {label2nd && (
                <>
                  <br />
                  <span className="ml-1 text-[8px] font-normal">
                    {label2nd}
                  </span>
                </>
              )}
            </>
          </div>
        );
      },
    },
    {
      accessorKey: "access",
      header: "Access",
      size: 70, // minimal width in px
      maxSize: 100,
      minSize: 70,
      Cell: ({ cell, row }) => {
        const type = row.original.type;
        const value = cell.getValue();
        if (type === "industry") {
          return <span>—</span>;
        }
        let color = "";
        let label = "";
        switch (value) {
          case "full":
            color = "bg-white text-blue-800 border-blue-300";
            label = "Full";
            break;
          case "workshops":
            color = "bg-white text-green-800 border-green-300";
            label = "Workshops";
            break;
          default:
            color = "bg-white text-gray-800 border-gray-300";
            label = value;
        }
        return (
          <span
            className={`px-2 py-1 rounded border text-xs font-semibold ${color}`}
          >
            {label}
          </span>
        );
      },
    },

    {
      header: "Name & Email",
      accessorFn: (row) => `${row.name ?? ""} ${row.email ?? ""}`,
      Cell: ({ row }) => (
        <div>
          <div>{row.original.name}</div>
          <div className="text-xs text-gray-500">{row.original.email}</div>
        </div>
      ),
    },
    {
      id: "most_recent_update",
      header: "Most Recent Update",
      accessorKey: "most_recent_update",
      cell: ({ getValue }) => {
        const raw = getValue();
        if (!raw) return "";

        const date = toDateAssumingUTC(raw);

        return date.toLocaleString(undefined, {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      id: "actions",
      header: "Actions",
      Cell: ({ row }) => (
        <div className="flex gap-2">
          <button
            onClick={() => loadUserData(row.original)}
            className="bg-blue-500 text-white rounded px-2 py-1 hover:bg-blue-600 transition-colors"
            style={{
              padding: "6px 12px",
              cursor: "pointer",
            }}
          >
            <Glasses className="h-3 w-4 inline-block mr-1" />
            View
          </button>
          {permissions.includes("ijcai_impersonate") && (
            <button
              onClick={() => handleAction(row.original)}
              style={{
                padding: "6px 12px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                background: "#f5f5f5",
                cursor: "pointer",
              }}
            >
              <UserIcon className="h-3 w-4 inline-block mr-1" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const handleAction = (row) => {
    axios
      .post(`${base_url}/api/admin/impersonate`, { id: row.code })
      .then((response) => setReload(reload + 1))
      .catch((error) => console.error("Error impersonating:", error));
  };

  const handlePrintBadges = async (location) => {
    try {
      const response = await axios.post(
        `${base_url}/api/admin/printbadges`,
        { location },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger the download
      const link = document.createElement("a");
      link.href = url;
      link.download = `badges.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setReload(reload + 1);
    } catch (error) {
      console.error("Error printing badges:", error);
    }
  };

  const loadUserData = async (row) => {
    try {
      setOpenModal(true);
      setLoadingData(true);
      const data = await axios.post(`${base_url}/api/admin/getuser`, {
        id: row.code,
      });
      setUserData(data.data);
      setLoadingData(false);
    } catch (error) {
      console.error("Error getting user", error);
      setOpenModal(false);
    }
  };

  const loadBadgeData = async (row) => {
    try {
      setOpenModalBadges(true);
      setLoadingData(true);
      const data = await axios.post(`${base_url}/api/admin/getuser`, {
        id: row.code,
      });
      setUserData(data.data);
      setLoadingData(false);
    } catch (error) {
      console.error("Error getting user", error);
      setOpenModalBadges(false);
    }
  };

  return (
    <>
      <div className="text-2xl mt-4 pb-4 font-bold flex gap-8 items-center">
        <User2Icon />
        Registrations
      </div>

      <div className="flex items-center gap-6 mb-4">
        {/* Type Filter */}

        {/* <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700">
            Filter by type:
          </span>
          {typeFilters.map((filter) => {
            let color = "";
            switch (filter.value) {
              case "industry":
                color = "bg-yellow-100 text-yellow-800 border-yellow-300";
                break;
              case "regular":
                color = "bg-blue-100 text-blue-800 border-blue-300";
                break;
              case "student":
                color = "bg-green-100 text-green-800 border-green-300";
                break;
              case "student-paper":
                color = "bg-purple-100 text-purple-800 border-purple-300";
                break;
              case "exhibitor":
                color = "bg-orange-100 text-orange-800 border-orange-300";
                break;
              default:
                color = "bg-white text-gray-700 border-gray-300";
            }

            const isActive = typeFilter === filter.value;
            return (
              <button
                key={filter.value}
                className={`px-3 py-1 rounded border text-sm font-semibold transition-colors
                  ${color}
                  ${isActive ? "ring-2 ring-blue-400" : ""}
                  ${filter.value === ""
                    ? "bg-white text-gray-700 border-gray-300"
                    : ""
                  }
                `}
                onClick={() => setTypeFilter(filter.value)}
              >
                {filter.label}
              </button>
            );
          })}
        </div> */}

        {/* Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700">
            Filter by Status:
          </span>
          {statusFilters.map((filter) => {
            let color = "";
            switch (filter.value) {
              case "Completed":
                color = "bg-blue-100 text-blue-800 border-blue-300";
                break;
              case "In progress":
                color = "bg-yellow-100 text-yellow-800 border-yellow-300";
                break;
              default:
                color = "bg-white text-gray-700 border-gray-300";
            }
            const isActive = statusFilter === filter.value;
            return (
              <button
                key={filter.value}
                className={`px-3 py-1 rounded border text-sm font-semibold transition-colors
                  ${color}
                  ${isActive ? "ring-2 ring-blue-400" : ""}
                  ${filter.value === ""
                    ? "bg-white text-gray-700 border-gray-300"
                    : ""
                  }
                `}
                onClick={() => setStatusFilter(filter.value)}
              >
                {filter.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-700">Actions:</span>

          <button
            onClick={() => {
              window.confirm(
                "This generates a PDF with all the badges with ✦ and marks the selected registrations as printed. Continue?"
              ) && handlePrintBadges();
            }}
            className="bg-blue-500 text-yellow-300 text-sm rounded px-2 py-1 hover:bg-blue-600 transition-colors"
          >
            Print ✦
          </button>
        </div>
      </div>

      <div style={{ flex: "1 1 0", paddingBottom: "20px" }}>
        <MaterialReactTable
          muiTableBodyCellProps={{
            sx: {
              padding: "12px 2px", // reduce vertical/horizontal padding
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              padding: "12px 2px",
            },
          }}
          columns={columns}
          data={filteredData}
        />
      </div>
      <RegistrationDetail
        open={openModal}
        loading={loadingData}
        data={userData}
        onClose={() => setOpenModal(false)}
      ></RegistrationDetail>
      <BadgeControl
        open={openModalBadges}
        loading={loadingData}
        data={userData}
        onClose={() => setOpenModalBadges(false)}
        onUpdate={() => handler()}
      />
    </>
  );
};

export default UserTable;
